import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Truck } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input, Label } from "@/components/ui/Input";
import { cn } from "@/lib/utils";

import { fetchInboundArrivals, INBOUND_ARRIVALS_QUERY_KEY, type InboundDockShipment } from "./inboundApi";
import {
  defaultArrivalsTimePeriod,
  filterInboundDockShipmentsByPeriod,
} from "./inboundArrivalDateFilter";
import {
  formatDockShipmentUnitCount,
  inboundArrivalManageUnitsHint,
} from "./inboundArrivalUnitKind";
import { InboundShipmentDetailModal } from "./InboundShipmentDetailModal";

function formatExpectedArrival(isoDate: string | null): string {
  if (isoDate == null || isoDate.trim() === "") return "Expected date unknown";
  const date = new Date(`${isoDate.trim()}T12:00:00`);
  if (Number.isNaN(date.getTime())) return `Expected ${isoDate.trim()}`;
  return `Expected ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

function shipmentSummary(shipment: InboundDockShipment): string {
  if (shipment.unit_count > 0) {
    return formatDockShipmentUnitCount(shipment.unit_count, shipment.units);
  }
  if (shipment.incoming_line_count != null && shipment.incoming_line_count > 1) {
    return `${shipment.incoming_line_count} lines`;
  }
  return shipment.expected_tracking ?? "";
}

function shipmentBadge(shipment: InboundDockShipment): { label: string; className: string } {
  if (shipment.unit_count > 0) {
    return {
      label: "Arrived",
      className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    };
  }
  return {
    label: "Expected",
    className: "bg-sky-50 text-sky-700 ring-sky-200",
  };
}

export function InboundArrivalLog({
  selectedPrinter,
  onRequestNew,
  onAddArrival,
  printNotice,
  onClearPrintNotice,
}: {
  selectedPrinter: string;
  onRequestNew: () => void;
  onAddArrival: (shipment: InboundDockShipment) => void;
  printNotice?: string | null;
  onClearPrintNotice?: () => void;
}): JSX.Element {
  const defaultPeriod = useMemo(() => defaultArrivalsTimePeriod(), []);
  const [periodFrom, setPeriodFrom] = useState(defaultPeriod.from);
  const [periodTo, setPeriodTo] = useState(defaultPeriod.to);
  const [selectedShipment, setSelectedShipment] = useState<InboundDockShipment | null>(null);

  const arrivalsQuery = useQuery({
    queryKey: INBOUND_ARRIVALS_QUERY_KEY,
    queryFn: () => fetchInboundArrivals(),
  });

  const allShipments = arrivalsQuery.data ?? [];
  const periodInvalid = periodFrom > periodTo;
  const shipments = useMemo(() => {
    if (periodInvalid) return [];
    return filterInboundDockShipmentsByPeriod(allShipments, periodFrom, periodTo);
  }, [allShipments, periodFrom, periodTo, periodInvalid]);
  const expectedCount = shipments.filter((shipment) => shipment.has_expectation && shipment.unit_count === 0).length;
  const arrivedCount = shipments.filter((shipment) => shipment.unit_count > 0).length;

  useEffect(() => {
    if (selectedShipment == null) return;
    const fresh = shipments.find((shipment) => shipment.id === selectedShipment.id);
    if (fresh == null) {
      setSelectedShipment(null);
      return;
    }
    if (
      fresh.unit_count !== selectedShipment.unit_count ||
      fresh.units.length !== selectedShipment.units.length
    ) {
      setSelectedShipment(fresh);
    }
  }, [shipments, selectedShipment]);

  return (
    <>
      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(16rem,18rem)_minmax(0,1fr)] lg:items-stretch">
        <Card className="flex h-full min-h-0 flex-col overflow-hidden">
          <CardHeader className="shrink-0">
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardBody className="min-h-0 flex-1 space-y-3 overflow-y-auto">
            <div className="space-y-1.5">
              <Label htmlFor="arrivals-period-from">From</Label>
              <Input
                id="arrivals-period-from"
                type="date"
                value={periodFrom}
                onChange={(event) => setPeriodFrom(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="arrivals-period-to">To</Label>
              <Input
                id="arrivals-period-to"
                type="date"
                value={periodTo}
                onChange={(event) => setPeriodTo(event.target.value)}
              />
            </div>
            {periodInvalid ? (
              <p className="text-sm text-rose-700">The From date must be on or before the To date.</p>
            ) : allShipments.length > shipments.length ? (
              <p className="text-xs text-slate-500">
                Showing {shipments.length} of {allShipments.length}
              </p>
            ) : null}
          </CardBody>
        </Card>

        <HubViewportCard chrome="filtersRail" className="flex min-h-0 flex-col">
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-slate-500" />
              Dock arrivals
              {expectedCount > 0 ? (
                <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-700">
                  {expectedCount} expected
                </span>
              ) : null}
              {arrivedCount > 0 ? (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                  {arrivedCount} arrived
                </span>
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardBody className="min-h-0 space-y-4 overflow-y-auto">
            {printNotice ? (
              <p role="status" className="flex items-center gap-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {printNotice}
              </p>
            ) : null}

            {arrivalsQuery.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading arrivals…
              </p>
            ) : arrivalsQuery.isError ? (
              <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
                <AlertTriangle className="h-4 w-4" />
                Couldn't load arrivals.{" "}
                <button type="button" className="underline" onClick={() => void arrivalsQuery.refetch()}>
                  Retry
                </button>
              </p>
            ) : periodInvalid ? (
              <p className="py-6 text-center text-sm text-slate-500">
                Choose a valid time period to see dock arrivals.
              </p>
            ) : shipments.length === 0 ? (
              <div className="space-y-3 py-6 text-center">
                <p className="text-sm text-slate-500">
                  No dock arrivals match this time period.
                </p>
                <Button type="button" onClick={onRequestNew}>
                  New dock arrival
                </Button>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {shipments.map((shipment) => {
                  const badge = shipmentBadge(shipment);
                  return (
                    <li key={shipment.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-slate-50/80"
                        onClick={() => setSelectedShipment(shipment)}
                      >
                        <div className="min-w-0">
                          <p className="truncate font-medium text-slate-900">
                            {shipment.po_number?.trim() ? `${shipment.po_number.trim()}, ` : null}
                            {shipment.vendor_name}
                            <span className="ml-2 text-sm font-normal text-slate-500">
                              {shipmentSummary(shipment)}
                            </span>
                          </p>
                          <p className="text-xs text-slate-400">
                            {shipment.unit_count > 0
                              ? inboundArrivalManageUnitsHint(shipment.units)
                              : formatExpectedArrival(shipment.expected_arrival)}
                          </p>
                          {shipment.notes != null && shipment.notes.trim() !== "" ? (
                            <p className="mt-0.5 truncate text-xs text-slate-600">{shipment.notes}</p>
                          ) : null}
                        </div>
                        <span
                          className={cn(
                            "shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset",
                            badge.className,
                          )}
                        >
                          {badge.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardBody>
        </HubViewportCard>
      </div>

      <InboundShipmentDetailModal
        open={selectedShipment != null}
        shipment={selectedShipment}
        selectedPrinter={selectedPrinter}
        printNotice={printNotice}
        onClearPrintNotice={onClearPrintNotice}
        onClose={() => setSelectedShipment(null)}
        onAddArrival={(shipment) => {
          setSelectedShipment(null);
          onAddArrival(shipment);
        }}
      />
    </>
  );
}
